import React from 'react';
import styled from 'styled-components';
import {
  Line,
  Icons,
  Container,
  Row,
  Column,
  Text,
  breakpoints,
  Box,
  space,
  Button,
  Header,
  variables,
} from '@generationtux/component-library';
import PreviewLayer from './PreviewLayer';
import { previewLayerStyles } from './previewLayerStyles';
import { bundles } from '../../../data/bundles';
import { useSpring, animated } from 'react-spring';
import { getCleanedCategory, scrollTop, getProductByCatalogNumber } from '../../../utils/utils';
import PreviewLayerThumbnails from '../preview/PreviewLayerThumbnails';
import { listCategories } from '../../../data/listCategories';
import CategoryIcon from '../../shared/CategoryIcon';

const StickyBox = styled(Box)`
  position: sticky;
  z-index: 100;
  bottom: ${space[4]}px;
  margin-top: ${space[4]}px;
  @media (min-width: ${breakpoints['xl']}) {
    margin-top: 0;
    bottom: 2vh;
  }
`;

const GridMobile = styled(Box)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  max-width: 100%;
  .thumbnail-wrap {
    max-width: 100%;
    width: 60px;
  }
  .thumbnail-wrap,
  svg {
    height: 60px;
  }
  > div {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-direction: column;
    transition: background-color 0.2s ease, transform 0.1s ease;
    cursor: pointer;
    border: 1px solid ${props => variables(props).gray};
    :active {
      transform: scale(0.9);
    }
    .banner {
      position: relative;
      left: 0;
      top: 0;
      transform: none;
      z-index: auto;
      .banner-label {
        background-color: transparent;
        padding: 4px;
        white-space: normal;
      }
    }
    .thumbnail-wrap,
    svg {
      flex-grow: 1;
    }
    @media (hover: hover) {
      &:hover {
        background-color: ${props => variables(props).white};
      }
    }
  }
`;

const PreviewExplodedStyled = styled(Box)`
  ${previewLayerStyles}
  width: calc(100vw);
  max-width: 1600px;
  position: relative;
  overflow: hidden;
  &:active {
    -webkit-tap-highlight-color: transparent;
  }
  img {
    opacity: 1 !important;
  }
  img[class*='shadows'] {
    visibility: hidden;
  }
  > div {
    position: relative;
    will-change: width;
    transform: translateZ(0);
  }
  [class*='exploded-layer-wrap'] {
    pointer-events: none;
    width: 30vw;
    max-width: ${1600 / 3.5}px;
    will-change: left;
    transform: translateZ(0);
    position: absolute;
    top: 0;
    img:first-child {
      position: relative;
    }
    &:hover {
      z-index: 1000;
    }
  }
  .exploded-image-wrap,
  .exploded-image-wrap-empty {
    transition: all 0.2s ease;
  }
  .exploded-image-wrap-empty {
    opacity: 0.6;
  }
  .hit-target {
    position: absolute;
    z-index: 500;
    pointer-events: auto;
    background: rgba(255, 0, 0, 0);
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: all 0.2s ease;
    // background: rgba(255,0,0,.1);
    &:active ~ .exploded-image-wrap,
    &:active ~ .exploded-image-wrap-empty {
      transform: scale(0.96);
    }
  }
  .banner {
    position: absolute;
    top: 0%;
    left: 50%;
    transform: translate3d(-50%, -100%, 0);
    transition: all 0.2s ease;

    z-index: -100;
    text-align: center;
    display: flex;
    align-items: center;
    flex-direction: column;
    .banner-label {
      transition: all 0.2s ease;
      background: ${props => variables(props).grayLight};
      color: ${props => variables(props).grayDarker};
      border-bottom: 1px solid ${props => variables(props).textPrimary};
      padding: 8px 8px;
      min-width: 32px;
      @media (min-width: ${props => breakpoints.lg}) {
        white-space: nowrap;
      }
    }
    &:after {
      content: '';
      width: 0;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 5px solid ${props => variables(props).textPrimary};
    }
  }
  @media (hover: hover) {
    .hit-target:hover ~ .exploded-image-wrap {
      filter: brightness(110%);
    }

    .hit-target:hover ~ .exploded-image-wrap-empty {
      opacity: 1;
    }
    .hit-target:hover .banner-label {
      background-color: ${props => variables(props).white};
    }
  }
  .exploded-layer-wrap-long-tie,
  .exploded-layer-wrap-tie-empty {
    left: -3%;
    transform: translateY(9%);
    .hit-target {
      top: 4%;
      left: 41%;
      right: 40%;
      bottom: 45%;
    }
    .exploded-image-wrap,
    .exploded-image-wrap-empty {
      transform-origin: center 10%;
    }
  }
  .exploded-layer-wrap-bow-tie {
    left: -3%;
    transform: translateY(9%);
    .hit-target {
      top: 4%;
      left: 41%;
      right: 40%;
      bottom: 86%;
    }
    .exploded-image-wrap,
    .exploded-image-wrap-empty {
      transform-origin: center 10%;
    }
  }
  .exploded-layer-wrap-shirt,
  .exploded-layer-wrap-shirt-empty {
    left: 22%;
    img[class*='shadows'] {
      visibility: visible;
    }
    .hit-target {
      top: 0%;
      left: 15%;
      right: 15%;
      bottom: 28%;
    }
    .exploded-image-wrap,
    .exploded-image-wrap-empty {
      transform-origin: center 20%;
    }
  }
  .exploded-layer-wrap-pocket-square {
    left: 0%;
    transform: translateY(15%);
    .hit-target {
      top: 20%;
      left: 58%;
      right: 30%;
      bottom: 72%;
    }
    [class*='shadow'] {
      display: none;
    }
  }
  .exploded-layer-wrap-pocket_square-empty {
    left: 0%;
    transform: translateY(15%);
    .hit-target {
      top: 20%;
      left: 58%;
      right: 30%;
      bottom: 68%;
    }
    [class*='shadow'] {
      display: none;
    }
  }
  .exploded-layer-wrap-pocket-square,
  .exploded-layer-wrap-pocket_square-empty {
    .exploded-image-wrap,
    .exploded-image-wrap-empty {
      transform-origin: 65% 20%;
    }
  }
  .exploded-layer-wrap-lapel-pin,
  .exploded-layer-wrap-lapel_pin-empty {
    left: 1%;
    .hit-target {
      top: 13%;
      left: 58%;
      right: 35%;
      bottom: 82%;
    }
    .exploded-image-wrap,
    .exploded-image-wrap-empty {
      transform-origin: 60% 10%;
    }
  }
  .exploded-layer-wrap-jacket,
  .exploded-layer-wrap-pants,
  .exploded-layer-wrap-jacket-empty {
    right: -5%;
    .hit-target {
      top: 0%;
      left: 5%;
      right: 5%;
      bottom: 25%;
    }
    .exploded-image-wrap,
    .exploded-image-wrap-empty {
      transform-origin: center 20%;
    }
  }
  .exploded-layer-wrap-pants {
    .banner {
      display: none;
    }
  }
  .exploded-layer-wrap-jacket {
    z-index: 10;
  }
  .exploded-layer-wrap-vest,
  .exploded-layer-wrap-vest-empty,
  .exploded-layer-wrap-cummerbund {
    left: 45%;
    .hit-target {
      top: 0%;
      left: 30%;
      right: 30%;
      bottom: 38%;
    }
  }
  .exploded-layer-wrap-vest,
  .exploded-layer-wrap-vest-empty {
    .exploded-image-wrap,
    .exploded-image-wrap-empty {
      transform-origin: center 20%;
    }
  }
  .exploded-layer-wrap-cummerbund {
    .exploded-image-wrap,
    .exploded-image-wrap-empty {
      transform-origin: center 50%;
    }
  }
  .exploded-layer-wrap-socks,
  .exploded-layer-wrap-socks-empty {
    position: relative; // need to set a layer that doesn't change position to relative to keep the parent height from collapsing

    img[class*='hanger'] {
      display: none;
    }
    .hit-target {
      top: 13%;
      left: 8%;
      right: 78%;
      bottom: 70%;
    }
    .exploded-image-wrap,
    .exploded-image-wrap-empty {
      transform-origin: 20% 10%;
    }
  }
  .exploded-layer-wrap-shoes,
  .exploded-layer-wrap-shoe-empty {
    .hit-target {
      top: 78%;
      left: 6%;
      right: 58%;
      bottom: 6%;
    }
    .exploded-image-wrap,
    .exploded-image-wrap-empty {
      transform-origin: 20% 80%;
    }
  }
  .exploded-layer-wrap-belt,
  .exploded-layer-wrap-belt-empty,
  .exploded-layer-wrap-suspenders {
    .hit-target {
      top: 82%;
      left: 49%;
      right: 29%;
      bottom: 7%;
    }
    .exploded-image-wrap,
    .exploded-image-wrap-empty {
      transform-origin: 60% 80%;
    }
  }
  .exploded-layer-wrap-cufflinks,
  .exploded-layer-wrap-cufflinks-empty {
    .hit-target {
      top: 82%;
      left: 75%;
      right: 8%;
      bottom: 7%;
    }
    .exploded-image-wrap,
    .exploded-image-wrap-empty {
      transform-origin: 80% 80%;
    }
  }
`;

const PreviewExploded = ({
  selectedCatalogNumbers,
  activeCollection,
  setActiveCollection,
  exploded,
  setExploded,
  mediaQueryXsMatches,
  ...props
}) => {
  const animationProps = useSpring({ opacity: 1, width: `100%`, from: { opacity: 0, width: '50%' } });

  return (
    <>
      <Box display="flex" justifyContent="center">
        <PreviewExplodedStyled
          py={[3, 4, 4, 4, 5, 5]}
          px={[3, 3, 4, 5, 5, 5]}
          exploded={exploded}
          onTouchStart={() => {}}
        >
          <Header type={1} size={4} headline mb={3}>
            Select an Item
          </Header>
          <Box display="none" displayXs="block">
            <Line headlineDivider />
          </Box>

          {mediaQueryXsMatches ? (
            <animated.div style={animationProps}>
              {selectedCatalogNumbers.map((category, i) => (
                <Box
                  key={i}
                  title={category.name}
                  className={`exploded-layer-wrap-${
                    category.activeItem
                      ? getCleanedCategory(getProductByCatalogNumber(category.activeItem))
                          .replace(/ /g, '-')
                          .toLowerCase()
                      : category.icon + '-empty'
                  }`}
                >
                  <Box
                    className="hit-target"
                    onClick={() => {
                      scrollTop();
                      setActiveCollection(category.name);
                      setExploded(!exploded);
                    }}
                  >
                    <Text as="span" size="xs" className="banner">
                      <span className="banner-label">{category.name}</span>
                    </Text>
                  </Box>

                  {selectedCatalogNumbers.map(
                    (item, i) =>
                      item.name === category.name && (
                        <div key={i} className={`exploded-image-wrap${!!item.activeItem ? '' : '-empty'}`}>
                          {item.activeItem ? (
                            <>
                              {item.bundleName &&
                                bundles.map(
                                  (bundle, i) =>
                                    item.bundleName === bundle.displayName && (
                                      <React.Fragment key={i}>
                                        <PreviewLayer
                                          product={getProductByCatalogNumber(bundle.products[1].catalogNumber)}
                                        />
                                      </React.Fragment>
                                    )
                                )}
                              <PreviewLayer product={getProductByCatalogNumber(item.activeItem)} />
                            </>
                          ) : (
                            <>
                              <img src={category.placeholder} alt="empty look" />
                            </>
                          )}
                        </div>
                      )
                  )}
                </Box>
              ))}
            </animated.div>
          ) : (
            <GridMobile>
              {listCategories.map((category, i) => (
                <Box
                  key={i}
                  title={category.name}
                  onClick={() => {
                    setActiveCollection(category.name);
                    setExploded(!exploded);
                    scrollTop();
                  }}
                >
                  <Text as="span" size="xs" className="banner" style={{ whiteSpace: 'nowrap' }}>
                    <span className="banner-label">{category.name}</span>
                  </Text>
                  {selectedCatalogNumbers.map(
                    (item, i) =>
                      item.name === category.name && (
                        <React.Fragment key={i}>
                          {item.activeItem ? (
                            <PreviewLayerThumbnails product={getProductByCatalogNumber(item.activeItem)} />
                          ) : (
                            <CategoryIcon category={category.category} color="textPrimary" />
                          )}
                        </React.Fragment>
                      )
                  )}
                </Box>
              ))}
            </GridMobile>
          )}
        </PreviewExplodedStyled>
      </Box>

      <StickyBox>
        <Container>
          <Row justifyContent="center">
            <Column column={12} columnMd={8}>
              <Box shadowLevel={3} backgroundColor="white" py={1} px={1} my={1} display="flex">
                <Button
                  py={[3, 3, 4, 4, 4, 4]}
                  px={['24px', '24px', 4, 4, 4, 4]}
                  buttonType="default"
                  outline
                  mr={1}
                  onClick={() => {
                    scrollTop();
                    setExploded(false);
                  }}
                >
                  <Icons.ArrowLeft />
                </Button>

                <Button
                  py={[3, 3, 4, 4, 4, 4]}
                  buttonType="info"
                  style={{ flexGrow: 1 }}
                  onClick={() => {
                    scrollTop();
                    setExploded(false);
                    setActiveCollection(null);
                  }}
                >
                  <Icons.Check />
                </Button>
              </Box>
            </Column>
          </Row>
        </Container>
      </StickyBox>
    </>
  );
};

export default PreviewExploded;
